"use client";

import { type JSX } from "react";
import { RichText } from "@sitecore-content-sdk/nextjs";
import Button from "../../atoms/Button";
import RichTextWithTable from "../../molecules/RichTextWithTable/RichTextWithTables";
import { detectPositionClass, extractHiddenBlock } from "../../utils/styles";
import { Default as Card, McClarenCard } from "../Card/Card";
import { CardStackProps } from "components/card-stack/card-stack.props";
import { firstNonEmpty } from "src/utils";

const getAlignmentStyles = (alignmentClass: string) => {
  switch (alignmentClass) {
    case "position-center":
      return "text-center!";
    case "position-right":
      return "text-right!";
    case "position-left":
      return "text-left";
    default:
      return "";
  }
};

const computeIsDarkBg = (rawStyles: string | undefined): boolean => {
  const stylesArray = rawStyles ? rawStyles.split(" ") : [];
  if (stylesArray.length === 0) return false;
  const darkBgColors = ["bg-dark-blue", "bg-ecolab-blue"];
  const bgColor = darkBgColors.find((cls) => stylesArray.includes(cls));
  return Boolean(
    (bgColor && bgColor !== "") || stylesArray.includes("text-white"),
  );
};

const CardStackDefaultComponent = (props: CardStackProps): JSX.Element => (
  <div
    className={`component card-stack ${props?.params?.Styles ?? ""} ${
      props?.fields ? "" : "emptyFields"
    }`}
  >
    <div className="component-content">
      <span className="is-empty-hint">Card Stack</span>
    </div>
  </div>
);

export const Default = (props: CardStackProps) => {
  console.log("---->[CardStack] Component", props?.fields?.data?.datasource);

  const { page, fields, params, rendering } = props;
  const isPageEditing = page?.mode?.isEditing;
  const id = params?.RenderingIdentifier;

  const datasource = fields?.data?.datasource;
  if (!datasource) return <CardStackDefaultComponent {...props} />;

  if (params?.FieldNames === "CardStackMcClaren") {
    return <CardStackMcClaren {...props} />;
  }

  const cards = datasource.Cards?.targetItems || [];
  const requestedColumnLayout =
    params?.["Column Layout"] != null
      ? `${params["Column Layout"]}`
      : "TwoColumnLayout";
  const isVerticleAlignment =
    params?.DisplayCardsVertically === "1" || cards.length === 1;
  const cardsPerRow = isVerticleAlignment
    ? "OneColumnLayout"
    : requestedColumnLayout;

  const alignmentClass = params?.Styles
    ? detectPositionClass(params.Styles)
    : "";
  const alignmentStyles = getAlignmentStyles(alignmentClass);

  const editModeBasedClasses = isPageEditing ? "block!" : "[&:empty]:hidden";
  const isDarkBg = computeIsDarkBg(params?.Styles);
  const textColor = isDarkBg ? "text-white" : "text-black";
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  const isTitleExists =
    isPageEditing || datasource.Title?.jsonValue?.value?.toString()?.trim();
  const isSubTitleExists =
    isPageEditing || datasource.SubTitle?.jsonValue?.value?.toString()?.trim();
  const isDescriptionExists =
    isPageEditing ||
    datasource.Description?.jsonValue?.value?.toString()?.trim();
  const titleAlignmentStyles = firstNonEmpty(
    alignmentStyles,
    "text-center! after:mx-auto",
  );

  const afterAlignmentClassMap: Record<string, string> = {
    "position-left": "after:mr-auto",
    "position-right": "after:ml-auto",
    "position-center": "after:mx-auto",
  };

  const afterAlignmentClass = afterAlignmentClassMap[alignmentClass] ?? "";

  let primaryBtnClass = "primary";
  let secondaryBtnClass = "secondary";
  if (isDarkBg) {
    primaryBtnClass = "primary-reverse";
    secondaryBtnClass = "secondary-reverse";
  }
  if (datasource.CTALink?.jsonValue?.value?.class) {
    primaryBtnClass = datasource.CTALink?.jsonValue?.value?.class;
  }
  if (datasource.SecondaryCTA?.jsonValue?.value?.class) {
    secondaryBtnClass = datasource.SecondaryCTA?.jsonValue?.value?.class;
  }

  return (
    <div
      className={`${params?.Styles ?? ""} ${hiddenBlockStyles}`}
      data-role="card-stack-component"
      id={id ?? undefined}
    >
      <div className="max-w-(--sitewide-container-max-width) mx-auto pt-5">
        {(isTitleExists || isSubTitleExists || isDescriptionExists) && (
          <div className={`my-8 px-4 ${editModeBasedClasses}`}>
            {isTitleExists && (
              <RichText
                field={datasource.Title?.jsonValue}
                tag="h2"
                className={`text-[28px] leading-normal tracking-[0.4px] lg:text-[32px] lg:leading-tight lg:tracking-[0.8px] font-medium after:content-[''] after:block after:clear-both after:w-[50%] lg:after:w-[17%] after:pt-3.75 after:border-b-2 after:border-teal ${textColor} ${titleAlignmentStyles} ${afterAlignmentClass} ${editModeBasedClasses}`}
              />
            )}
            {isSubTitleExists && (
              <RichText
                field={datasource.SubTitle?.jsonValue}
                className={`text-[24px] font-medium leading-[1.1] text-center ${textColor} ${
                  alignmentStyles || "text-center"
                } ${editModeBasedClasses}`}
                tag="h3"
              />
            )}
            {isDescriptionExists && (
              <RichTextWithTable
                field={datasource.Description?.jsonValue}
                className={`richtext-content mt-5 ${textColor} ${alignmentStyles || ""}`}
              />
            )}
          </div>
        )}
        <div
          className={`px-4 pb-8 cardStackContainer ${
            isVerticleAlignment ? "pb-9!" : "pt-6"
          }`}
        >
          <div className={`flex gap-8 flex-wrap ${cardsPerRow} `}>
            {cards.map((card, i) => (
              <Card
                key={card.id}
                page={page}
                rendering={rendering}
                fields={{
                  data: {
                    datasource: card,
                  },
                }}
                params={{
                  isVerticleAlignment: `${isVerticleAlignment}`,
                  isDarkBg: `${isDarkBg}`,
                  index: `${i}`,
                  TypeOfTitleTag: params?.TypeOfTitleTag?.toLowerCase(),
                  TypeOfSubtitleTag: params?.TypeOfSubtitleTag?.toLowerCase(),
                  isPartOfCardStack: "true",
                  DoNotStretchCardImage: `${
                    params?.DoNotStretchCardImage === "1" ||
                    params?.Styles?.includes("auto-image-width")
                  }`,
                }}
              />
            ))}
          </div>

          {(isPageEditing ||
            datasource.CTALink?.jsonValue?.value?.href ||
            datasource.SecondaryCTA?.jsonValue?.value?.href) && (
            <div className="flex items-center justify-center flex-wrap mt-8 gap-y-4">
              {(isPageEditing ||
                datasource.CTALink?.jsonValue?.value?.href) && (
                <Button
                  field={datasource.CTALink?.jsonValue}
                  buttonType={primaryBtnClass}
                  iconName="CHEVRON_BLUE_RIGHT"
                  className="mb-1!"
                  data-role="link"
                />
              )}
              {(isPageEditing ||
                datasource.SecondaryCTA?.jsonValue?.value?.href) && (
                <Button
                  field={datasource.SecondaryCTA?.jsonValue}
                  buttonType={secondaryBtnClass}
                  iconName="CHEVRON_BLUE_RIGHT"
                  className="mb-1! mr-0!"
                  data-role="link"
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const CardStackMcClaren = (props: CardStackProps): JSX.Element => {
  console.log(
    "---->[CardStackMcClaren] Component",
    props?.fields?.data?.datasource,
  );

  const { page, fields, params, rendering } = props;
  const isPageEditing = page?.mode?.isEditing;
  const id = params?.RenderingIdentifier;

  const datasource = fields?.data?.datasource;
  if (!datasource) return <CardStackDefaultComponent {...props} />;

  const cards = datasource.Cards?.targetItems || [];
  const requestedColumnLayout =
    params?.["Column Layout"] != null
      ? `${params["Column Layout"]}`
      : "TwoColumnLayout";
  const isVerticleAlignment =
    params?.DisplayCardsVertically === "1" || cards.length === 1;
  const cardsPerRow = isVerticleAlignment
    ? "OneColumnLayout"
    : requestedColumnLayout;

  const editModeBasedClasses = isPageEditing ? "block!" : "[&:empty]:hidden";

  const alignmentClass = params?.Styles
    ? detectPositionClass(params.Styles)
    : "";
  const alignmentStyles = getAlignmentStyles(alignmentClass);

  const isDarkBg = computeIsDarkBg(params?.Styles);
  const textColor = isDarkBg ? "text-white" : "text-black";
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  const isTitleExists =
    isPageEditing || datasource.Title?.jsonValue?.value?.toString()?.trim();
  const isSubTitleExists =
    isPageEditing || datasource.SubTitle?.jsonValue?.value?.toString()?.trim();
  const isDescriptionExists =
    isPageEditing ||
    datasource.Description?.jsonValue?.value?.toString()?.trim();
  const titleAlignmentStyles = alignmentStyles || "text-center! after:mx-auto";

  const afterAlignmentClassMap: Record<string, string> = {
    "position-left": "after:mr-auto",
    "position-right": "after:ml-auto",
    "position-center": "after:mx-auto",
  };

  const afterAlignmentClass = afterAlignmentClassMap[alignmentClass] ?? "";

  let primaryBtnClass = "primary";
  let secondaryBtnClass = "secondary";
  if (isDarkBg) {
    primaryBtnClass = "primary-reverse";
    secondaryBtnClass = "secondary-reverse";
  }
  if (datasource.CTALink?.jsonValue?.value?.class) {
    primaryBtnClass = datasource.CTALink.jsonValue.value.class;
  }
  if (datasource.SecondaryCTA?.jsonValue?.value?.class) {
    secondaryBtnClass = datasource.SecondaryCTA.jsonValue?.value.class;
  }

  return (
    <div
      className={`${params?.Styles ?? ""} ${hiddenBlockStyles}`}
      data-role="card-stack-component"
      id={id ?? undefined}
    >
      <div className="max-w-(--sitewide-container-max-width) mx-auto pt-5">
        {(isTitleExists || isSubTitleExists || isDescriptionExists) && (
          <div className={`my-8 px-4 ${editModeBasedClasses}`}>
            {isTitleExists && (
              <RichText
                field={datasource.Title?.jsonValue}
                tag="h2"
                className={`leading-[0.9] **:leading-[0.9] lg:leading-[0.8] lg:**:leading-[0.8] text-[56px] **:text-[56px] lg:text-[93px] lg:**:text-[93px] font-['Special_Gothic_Condensed_One','Roboto'] [text-shadow:0_0_.7em_#fff] tracking-[0.4px] lg:tracking-[0.8px] font-medium mb-5 ${textColor} ${titleAlignmentStyles} ${afterAlignmentClass} ${editModeBasedClasses}`}
              />
            )}
            {isSubTitleExists && (
              <RichText
                field={datasource.SubTitle?.jsonValue}
                className={`text-[24px] font-medium leading-[1.1] ${textColor} ${editModeBasedClasses} ${
                  alignmentStyles || "text-center"
                } ${editModeBasedClasses}`}
                tag="h3"
              />
            )}
            {isDescriptionExists && (
              <RichTextWithTable
                field={datasource.Description?.jsonValue}
                className={`richtext-content mt-5 ${textColor} ${editModeBasedClasses} ${
                  alignmentStyles || ""
                }`}
              />
            )}
          </div>
        )}
        <div
          className={`pb-8 cardStackContainer ${
            isVerticleAlignment ? "pb-9!" : "pt-6 px-4"
          }`}
        >
          <div className={`flex gap-8 flex-wrap ${cardsPerRow}`}>
            {cards.map((card, i) => (
              <McClarenCard
                key={card.id}
                page={page}
                rendering={rendering}
                fields={{
                  data: {
                    datasource: card,
                  },
                }}
                params={{
                  isVerticleAlignment: `${isVerticleAlignment}`,
                  isDarkBg: `${isDarkBg}`,
                  index: `${i}`,
                  TypeOfTitleTag: params?.TypeOfTitleTag?.toLowerCase(),
                  TypeOfSubtitleTag: params?.TypeOfSubtitleTag?.toLowerCase(),
                  isPartOfCardStack: "true",
                }}
              />
            ))}
          </div>

          {(isPageEditing ||
            datasource.CTALink?.jsonValue?.value?.href ||
            datasource.SecondaryCTA?.jsonValue?.value?.href) && (
            <div className="flex items-center justify-center mt-8 gap-x-3 gap-y-4">
              {(isPageEditing ||
                datasource.CTALink?.jsonValue?.value?.href) && (
                <Button
                  field={datasource.CTALink?.jsonValue}
                  buttonType={primaryBtnClass}
                  iconName="CHEVRON_BLUE_RIGHT"
                  className="mb-1!"
                  data-role="link"
                />
              )}
              {(isPageEditing ||
                datasource.SecondaryCTA?.jsonValue?.value?.href) && (
                <Button
                  field={datasource.SecondaryCTA?.jsonValue}
                  buttonType={secondaryBtnClass}
                  iconName="CHEVRON_BLUE_RIGHT"
                  className="mb-1!"
                  data-role="link"
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
